import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useDisplay } from 'vuetify';

import useReaderStore from '../stores/reader';
import useRenditionStore from '../stores/rendition';
import useNavigationStore from '../stores/navigation';
import useMediaStore from '../stores/media';
import useUiStore from '../stores/ui';
import useSettingStore from '../stores/setting';

import type { GoToItem } from '../types';
import { parseLocation } from '../util/location';
import { hasWidthForSpreads } from '../util/book';
import useNavigationControls from './use-navigation-controls';
import useNavigationState from './use-navigation-state';
import useNavigationTriggerStore from '../stores/navigation-trigger';

const useReaderNavigation = () => {
  const { platform } = useDisplay();
  const navState = useNavigationState();
  const { flags, manualNavigation, triggerFlag, tempSuppressUI } = navState;
  const { navigateForward, navigateBack } = useNavigationControls();
  const { setNavigationTrigger } = useNavigationTriggerStore();

  const { isContinuous } = storeToRefs(useReaderStore());
  const { isPageSlideAnimationEnabled, isPageTurnAnimationEnabled } = storeToRefs(useSettingStore());
  const { setAutoFollowPlayback } = useSettingStore();
  const { toggleVisibleGoToStart, resetPlayerTransform } = useUiStore();

  const renditionStore = useRenditionStore();
  const { isRenditionInitialized, isReflowable, isRTL, locations, isRightPageOnLeftSide, currentRenditionLocation } =
    storeToRefs(renditionStore);
  const { goToPage, goToPreviousPage, goToNextPage, goToPreviousSpine, goToNextSpine } = renditionStore;

  const navigationStore = useNavigationStore();
  const { currentPositionPct, currentSpineIndex, totalSpineItems, currentAudioTrackHref, currentAudioTrackCFI } =
    storeToRefs(navigationStore);
  const { setNavigating, setScrubbing } = navigationStore;

  const mediaStore = useMediaStore();
  const { requestedPlayerPosition, isPlaying } = storeToRefs(mediaStore);
  const { setCurrentTrack, setIsUserChangingTrack, setIsLastTrack } = mediaStore;

  const isSingleFixedPageMode = computed(
    () => !isReflowable.value && (!hasWidthForSpreads(platform.value.touch) || isRightPageOnLeftSide.value),
  );

  const reflowablePosition = computed(() =>
    isRTL.value ? Math.abs(currentPositionPct.value) : currentPositionPct.value,
  );

  // When page turn animation (FlipperManager) is enabled, we MUST use goToNextSpine/goToPreviousSpine
  // which call rendition.next()/prev() to trigger the FlipperManager animation
  // If we use goToNextPage/goToPreviousPage (fixed logic), it calls rendition.display() which bypasses FlipperManager
  const useFixedLogic = computed(
    () => isSingleFixedPageMode.value && !isPageSlideAnimationEnabled.value && !isPageTurnAnimationEnabled.value,
  );
  /**
   * Is the reader at the start of the book?
   * Reads from stores: position tracking + rendition state
   */
  const isAtStart = computed(() => {
    if (!isRenditionInitialized.value) return false;

    const spineIdx = currentSpineIndex.value;

    if (spineIdx === 0) {
      // For reflowable books, also check percentage
      if (isReflowable.value) {
        return Math.round(reflowablePosition.value) <= 1;
      }
      return true;
    }
    return false;
  });

  /**
   * Is the reader at the end of the book?
   * Reads from stores: position tracking + rendition state
   */
  const isAtEnd = computed(() => {
    if (!isRenditionInitialized.value) return false;

    const location = currentRenditionLocation.value;
    // First check rendition's atEnd flag if available
    if (location?.atEnd) return true;

    const spineIdx = currentSpineIndex.value;
    const totalSpine = totalSpineItems.value;

    if (isReflowable.value) {
      return Math.round(reflowablePosition.value) >= 99;
    }

    // For fixed layouts, handle spread vs. single-page thresholds
    // isSingleFixedPageMode is reactive to screen size changes
    const threshold = isSingleFixedPageMode.value ? 1 : 2;
    return spineIdx >= totalSpine - threshold;
  });

  /**
   * Centralizes side-effects (Media reset, UI toggles, Error handling)
   * used by all navigation types.
   */
  const executeNavigation = async (navAction: () => Promise<any>, flag?: keyof typeof flags.value) => {
    if (flag) {
      triggerFlag(flag);
    }

    toggleVisibleGoToStart(false);
    setCurrentTrack(undefined);
    // Only signal "keep playing" if audio was already running — never auto-start on navigation.
    if (isPlaying.value) {
      setIsUserChangingTrack(true);
    }
    setNavigating(true);

    try {
      await navAction();
    } catch (err) {
      console.error('Navigation failed:', err);
    } finally {
      setNavigating(false);
    }
  };

  const setAudioOffsetFromCfi = (cfi: string) => {
    const { temporalOffsetSeconds } = parseLocation(cfi);
    if (temporalOffsetSeconds) {
      requestedPlayerPosition.value = temporalOffsetSeconds;
    }
  };

  const goToLocation = (newValue: number) => {
    const value = isRTL.value ? Math.abs(newValue) : newValue;
    const isGoingForwards = value > currentPositionPct.value;

    const target = isReflowable.value && locations.value ? locations.value.cfiFromPercentage(value / 100) : value;

    return executeNavigation(() => goToPage({ target, isGoingForwardsInBook: isGoingForwards }), 'location');
  };

  const goToContent = (item: GoToItem) => {
    if (typeof item.target === 'string') setAudioOffsetFromCfi(item.target);
    return executeNavigation(() => goToPage(item), 'toc');
  };

  const goToBookmark = (item: GoToItem) => {
    if (typeof item.target === 'string') setAudioOffsetFromCfi(item.target);
    setNavigationTrigger('BOOKMARK');
    return executeNavigation(() => goToPage(item), 'bookmark');
  };

  /**
   * Scrolls to the current playback position without changing audio track
   * Used by "Go to Playback" button in free scroll mode
   */
  const goToPlayback = () => {
    const target = currentAudioTrackCFI.value || currentAudioTrackHref.value;
    if (!target) {
      console.warn('No target found for goToPlayback');
      return Promise.reject();
    }

    // Don't clear current track or set isUserChangingTrack
    // Just scroll to the position
    setNavigationTrigger('GO_TO_PLAYBACK');
    toggleVisibleGoToStart(false);
    setNavigating(true);
    setAutoFollowPlayback(true);

    return goToPage({ target, isGoingForwardsInBook: true })
      .catch((err) => {
        console.error('Navigation to playback failed:', err);
      })
      .finally(() => {
        setNavigating(false);
      });
  };

  const goToAnnotation = (item: GoToItem) => {
    if (typeof item.target === 'string') setAudioOffsetFromCfi(item.target);
    setNavigationTrigger('ANNOTATION');
    return executeNavigation(() => goToPage(item), 'annotation');
  };

  const goToNext = () => {
    setScrubbing(null);
    tempSuppressUI();

    // TODO: move to navigation controls and make continuous specific. Use view.visible property instead.
    if (isContinuous.value) return navigateForward();

    // Reset current track before switching page, to avoid playing previous
    // page track during fast page switching (only in paginated books).
    setCurrentTrack(undefined);

    manualNavigation.value = true;
    resetPlayerTransform();

    if (useFixedLogic.value) {
      return isRTL.value ? goToPreviousPage() : goToNextPage();
    }
    return isRTL.value ? goToPreviousSpine() : goToNextSpine();
  };

  const goToPrevious = () => {
    setScrubbing(null);
    setIsLastTrack(false);
    tempSuppressUI();

    // TODO: move to navigation controls and make continuous specific. Use view.visible property instead.
    if (isContinuous.value) return navigateBack();

    // Reset current track before switching page, to avoid playing previous
    // page track during fast page switching (only in paginated books).
    setCurrentTrack(undefined);

    manualNavigation.value = true;
    resetPlayerTransform();

    if (useFixedLogic.value) {
      return isRTL.value ? goToNextPage() : goToPreviousPage();
    }
    return isRTL.value ? goToNextSpine() : goToPreviousSpine();
  };

  return {
    ...navState,
    isAtStart,
    isAtEnd,
    isSingleFixedPageMode,
    reflowablePosition,
    goToLocation,
    goToContent,
    goToBookmark,
    goToPlayback,
    goToAnnotation,
    goToNext,
    goToPrevious,
    setAudioOffsetFromCfi,
  };
};

export default useReaderNavigation;
